
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { AppShell } from './components/shell/AppShell';
import { DashboardView } from './components/modules/Dashboard';
import { POSView } from './components/modules/POS';
import { MarketplaceView } from './components/modules/Marketplace';
import { StorefrontView } from './components/modules/Storefront';
import { EscrowView } from './components/modules/Escrow';
import { ChatView } from './components/modules/Chat';
import { WalletView } from './components/modules/Wallet';
import { PortfolioView } from './components/modules/Portfolio';
import { RewardsView } from './components/modules/Rewards';
import { ManagementView } from './components/modules/Management';
import { LegalView } from './components/modules/Legal';
import { AboutPageView } from './components/modules/AboutPage';
import { SettingsView } from './components/modules/Settings';
import { GhostCardView } from './components/modules/GhostCard';
import { GhostCardDevView } from './components/modules/GhostCardDev';
import { DocsViewer } from './components/docs/DocsViewer';
import { CartDrawer } from './components/modules/cart/CartDrawer';
import { TransactionHistoryView } from './components/modules/TransactionHistory';
import { SwapView } from './components/modules/Swap';
import { BridgeView } from './components/modules/Bridge';
import { UserDetailsView } from './components/modules/UserDetails';
import { InvoiceView } from './components/modules/Invoice';
import { GovernanceView } from './components/modules/Governance';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WalletProvider } from './context/WalletContext';
import { DemoProvider } from './context/DemoContext';
import { ThemeProvider } from './context/ThemeContext';
import { CartProvider, useCart } from './context/CartContext';
import { FeatureProvider } from './context/FeatureContext';
import { WalletConnectModal } from './components/wallet/WalletConnect';
import { ViewState, User } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { LandingPage } from './components/landing/LandingPage';
import { SolutionsPage } from './components/landing/SolutionsPage';
import { GhostCardLanding } from './components/landing/GhostCardLanding';
import { AboutLanding } from './components/landing/AboutLanding';
import { LegalPage } from './components/landing/LegalPage';
import { LogonForm } from './components/auth/LogonForm';
import { DemoVideoModal } from './components/landing/DemoVideoModal';

const MainApp = () => {
    const [currentView, setCurrentView] = useState<ViewState>('dashboard');
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const { isCartOpen, closeCart } = useCart();

    const renderView = () => {
        switch (currentView) {
            case 'dashboard': return <DashboardView onNavigate={setCurrentView} />;
            case 'pos': return <POSView />;
            case 'marketplace': return <MarketplaceView />;
            case 'storefront': return <StorefrontView />;
            case 'escrow': return <EscrowView />;
            case 'chat': return <ChatView />;
            case 'wallet': return <WalletView onNavigate={setCurrentView} />;
            case 'portfolio': return <PortfolioView />;
            case 'transaction-history': return <TransactionHistoryView onNavigate={setCurrentView} />;
            case 'rewards': return <RewardsView />;
            case 'management': return <ManagementView onNavigate={setCurrentView} onSelectUser={setSelectedUser} />;
            case 'user-details': return <UserDetailsView user={selectedUser} onNavigate={setCurrentView} />;
            case 'legal': return <LegalView />;
            case 'about': return <AboutPageView />;
            case 'settings': return <SettingsView />;
            case 'card': return <GhostCardView />;
            case 'card-dev': return <GhostCardDevView />;
            case 'invoice': return <InvoiceView />;
            case 'swap': return <SwapView />;
            case 'bridge': return <BridgeView />;
            case 'governance': return <GovernanceView />;
            case 'docs': return <DocsViewer onBack={() => setCurrentView('dashboard')} initialPage="introduction" />;
            case 'api-reference': return <DocsViewer onBack={() => setCurrentView('dashboard')} initialPage="api-reference" />;
            case 'brand-guide': return <DocsViewer onBack={() => setCurrentView('dashboard')} initialPage="brand-guide" />;
            default: return <DashboardView onNavigate={setCurrentView} />;
        }
    };

    return (
        <AppShell 
            currentView={currentView} 
            onNavigate={setCurrentView}
            onConnectWallet={() => setShowWalletModal(true)}
            isSidebarCollapsed={isSidebarCollapsed}
            onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentView}
                    initial={{ opacity: 0, y: 10, scale: 0.99 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.99 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="h-full"
                >
                    {renderView()}
                </motion.div>
            </AnimatePresence>
            <AnimatePresence>
                {isCartOpen && <CartDrawer onClose={closeCart} />}
            </AnimatePresence>
            {showWalletModal && <WalletConnectModal onClose={() => setShowWalletModal(false)} />}
        </AppShell>
    );
};

const AppContent = () => {
    const { user } = useAuth();
    const [showLogonModal, setShowLogonModal] = useState(false);
    const [showDemoModal, setShowDemoModal] = useState(false);
    const [page, setPage] = useState<'landing' | 'docs' | 'solutions' | 'card-landing' | 'about-landing' | 'legal'>('landing');
    const [docsSection, setDocsSection] = useState('introduction');

    const handleNavigateToDocs = (section: string = 'introduction') => {
        setDocsSection(section);
        setPage('docs');
    };

    if (user) {
        return <MainApp />;
    }

    const renderPage = () => {
        switch(page) {
            case 'landing':
                return (
                    <LandingPage 
                        onLogin={() => setShowLogonModal(true)} 
                        onWatchDemo={() => setShowDemoModal(true)}
                        onNavigateToDocs={handleNavigateToDocs}
                        onNavigate={(p) => setPage(p as any)}
                    />
                );
            case 'solutions':
                return (
                    <SolutionsPage 
                        onLogin={() => setShowLogonModal(true)}
                        onNavigateToDocs={handleNavigateToDocs}
                        onNavigate={(p) => setPage(p as any)}
                    />
                );
            case 'card-landing':
                return (
                    <GhostCardLanding 
                        onLogin={() => setShowLogonModal(true)}
                        onNavigateToDocs={handleNavigateToDocs}
                        onNavigate={(p) => setPage(p as any)}
                    />
                );
            case 'about-landing':
                return (
                    <AboutLanding 
                        onLogin={() => setShowLogonModal(true)}
                        onNavigateToDocs={handleNavigateToDocs}
                        onNavigate={(p) => setPage(p as any)}
                    />
                );
            case 'legal':
                return (
                    <LegalPage 
                        onLogin={() => setShowLogonModal(true)}
                        onNavigateToDocs={handleNavigateToDocs}
                        onNavigate={(p) => setPage(p as any)}
                    />
                );
            case 'docs':
                return (
                    <div className="bg-[#F8F9FA] dark:bg-[#0A0B0F] min-h-screen">
                        <div className="p-4 md:p-8 lg:px-12">
                           <DocsViewer onBack={() => setPage('landing')} isPublic={true} initialPage={docsSection} />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            {renderPage()}
           
            <AnimatePresence>
                {showLogonModal && <LogonForm onClose={() => setShowLogonModal(false)} />}
            </AnimatePresence>
            <AnimatePresence>
                {showDemoModal && <DemoVideoModal onClose={() => setShowDemoModal(false)} />}
            </AnimatePresence>
        </>
    );
}

const App = () => {
    return (
        <ThemeProvider>
            <AuthProvider>
                <WalletProvider>
                    <CartProvider>
                        <FeatureProvider>
                            <DemoProvider>
                                <AppContent />
                            </DemoProvider>
                        </FeatureProvider>
                    </CartProvider>
                </WalletProvider>
            </AuthProvider>
        </ThemeProvider>
    );
};

export default App;
